import { GraphComponent, IRenderTreeElement } from '@ardoq/yfiles';
import { MutableRefObject, useEffect, useId, useMemo, useRef } from 'react';
import { BackgroundVisualGroup, Node } from './props';
import { constructBackgroundVisual } from './yfilesConstructors';
import type { GraphBuilderSources } from './useYFiles';

type UseBackgroundVisualsArgs = {
  graphComponentRef: MutableRefObject<GraphComponent | null>;
  graphBuilderSourcesRef: MutableRefObject<GraphBuilderSources | null>;
  nodes: Node[];
  backgroundGroups: BackgroundVisualGroup[] | undefined;
};

const toMembershipKey = (nodes: Node[]): string =>
  nodes
    .map(node => node.id)
    .sort()
    .join('|');

export const useBackgroundVisuals = ({
  graphComponentRef,
  graphBuilderSourcesRef,
  nodes,
  backgroundGroups,
}: UseBackgroundVisualsArgs): void => {
  const instanceId = useId();
  const elementsRef = useRef<IRenderTreeElement[]>([]);
  const nodeMembershipKey = useMemo(() => toMembershipKey(nodes), [nodes]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    const builder = graphBuilderSourcesRef.current?.builder;
    if (!graphComponent || !builder) return;
    const renderTree = graphComponent.renderTree;

    if (!backgroundGroups?.length) return;

    for (const group of backgroundGroups) {
      const element = renderTree.createElement(
        renderTree.backgroundGroup,
        constructBackgroundVisual(group, builder, instanceId)
      );
      elementsRef.current.push(element);
    }

    // The render tree was mutated outside yFiles' own update flow; trigger a
    // redraw so the new background visuals paint.
    graphComponent.invalidate();

    return () => {
      for (const element of elementsRef.current) {
        try {
          renderTree.remove(element);
        } catch {
          // render tree may already be torn down on remount
        }
      }
      elementsRef.current = [];
    };
  }, [
    backgroundGroups,
    nodeMembershipKey,
    instanceId,
    graphComponentRef,
    graphBuilderSourcesRef,
  ]);
};
